import { depictMerchant } from "@/lib/depict-client/depict-merchant"
import { transformDepictSearchResponse } from "@/lib/depict-client/helpers/transformDepictSearchResponse"
import { type ISearchService } from "@/lib/depict-client/interfaces/ISearchService"
import { type BaseSearchRequestV3 } from "@/lib/depict-client/types/api/BaseSearchRequestV3"
import { type RecommendResponseV3 } from "@/lib/depict-client/types/api/RecommendResponseV3"
import { type SearchRequestV3 } from "@/lib/depict-client/types/api/SearchRequestV3"
import { type GroupedSearchResponse } from "@/lib/depict-client/types/api/SearchResponse"
import { type SearchSuggestionsRequestV3 } from "@/lib/depict-client/types/api/SearchSuggestionsRequestV3"
import { type SearchSuggestionsResponseV3 } from "@/lib/depict-client/types/api/SearchSuggestionsResponseV3"
import { type ClientFetchService } from "@/lib/http/ClientFetchService"
import { type FetchService } from "@/lib/http/FetchService"
import type Logger from "@/lib/logger/Logger"
import { type Result, ResultFactory, ResultUtil } from "@/types/generic/Result"
import { stringify } from "neoqs"

export class SearchApiService implements ISearchService {
    constructor(
        private fetchService: FetchService | ClientFetchService,
        private logger: Logger
    ) {}

    /**
     * Returns products and content that matches a given query, optionally filtered.
     * @param {SearchRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/search-v3/get-results
     */
    public getResults = async (
        req: SearchRequestV3,
        paramsLocale?: string
    ): Promise<Result<GroupedSearchResponse>> => {
        try {
            const response = await this.fetchService.fetch("/search/results", {
                method: "POST",
                body: JSON.stringify({ ...req, merchant: depictMerchant })
            })

            if (ResultUtil.isSuccess(response)) {
                const transformedResponse = transformDepictSearchResponse({
                    response,
                    locale: paramsLocale,
                    slimVersion: true
                })
                return ResultFactory.success(transformedResponse)
            } else {
                return (
                    this.logger.error("Failed to fetch search results"),
                    ResultFactory.failure(new Error("Failed to fetch search results"))
                )
            }
        } catch (error) {
            return (
                this.logger.error(
                    `Server error for search results, with message: ${error}`
                ),
                ResultFactory.failure(
                    error instanceof Error ? error : new Error("Unknown error")
                )
            )
        }
    }

    /**
     * Returns suggestions for a given query. Call this while the user is typing to help the user find what they're looking for.
     * The suggestions are of two types:
     * - suggestions for search queries that are related to the current query.
     * - suggestions for listings that are related to the current query.
     * @param {SearchSuggestionsRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/search-v3/get-suggestions
     */
    public getSuggestions = async (
        req: SearchSuggestionsRequestV3
    ): Promise<Result<SearchSuggestionsResponseV3>> => {
        return this.fetchService.fetch(
            "/search/suggestions?" + stringify({ ...req, merchant: depictMerchant }),
            {
                method: "GET"
            }
        )
    }

    /**
     * Returns recommendations for products related to the search query.
     * It should be called once the user has exhausted the search results for their query and the recommended products are typically shown below the search results.
     * @param {BaseSearchRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/search-v3/get-related-recommendations
     */
    public getRelatedRecommendations = async (
        req: BaseSearchRequestV3
    ): Promise<Result<RecommendResponseV3>> => {
        return this.fetchService.fetch("/search/related", {
            method: "POST",
            body: JSON.stringify({ ...req, merchant: depictMerchant })
        })
    }
}
